import { isContractWithClient } from "app/utils/contractState";

/**
 * The line a locked field shows instead of an input. Keyed by what did the
 * locking, because the inspector's next move differs: a contract lock is
 * cleared by revoking, a payment lock by cancelling the request.
 */
export const LOCK_NOTICES = {
  contract: "Not editable after contract sent to client.",
  payment: "Not editable after payment sent to client.",
  both: "Not editable after contract sent & payment sent to client.",
  signed: "Not editable after contract signed.",
  inspected: "Not editable after inspection starts or is completed.",
};

/**
 * Whether a contract is live enough to freeze what it quotes.
 *
 * declined and delivery_failed are excluded on purpose: nothing binding exists,
 * and for a bounced email the recovery is to correct the very client details a
 * lock would freeze.
 */
export const isContractSent = (status) => isContractWithClient(status);

/**
 * Payment states that have reached the client and are not finished.
 *
 * disputed and disputed_review are live money being contested, and no send is
 * offered for them, so an editable quote can only end with the job disagreeing
 * with the amount under dispute. refunded and dispute_lost are settled: a new
 * payment can start from them, and it needs a new number.
 */
export const PAYMENT_SENT_STATUSES = [
  "unpaid",
  "paid_manual",
  "paid_stripe",
  "disputed",
  "disputed_review",
];

/** Whether a payment the client has seen is still holding the amount. */
export const isPaymentSent = (status) => PAYMENT_SENT_STATUSES.includes(status);

/**
 * Which LockHintDialog a locked field opens, so the dialog can name the unlock
 * path. "signed" has no dialog of its own: the way out is still revoking.
 */
export const hintVariantForNotice = (notice) => {
  if (notice === "both") return "both";
  if (notice === "payment") return "payment";
  return "contract";
};

/**
 * Which fields the Job Booking and Job Setup modals must not let you edit.
 *
 * A contract that is already with the client quotes the appointment, address,
 * client name and price. Change one here and the document they are about to
 * sign describes a job that no longer exists.
 *
 * Client email stays editable on purpose. A typo there is exactly what you
 * need to fix when a contract bounces.
 */
export const computeFieldLocks = ({ contractStatus, paymentStatus } = {}) => {
  const contractLocked = isContractSent(contractStatus);
  const paymentLocked = isPaymentSent(paymentStatus);
  const byContract = contractLocked ? "contract" : null;

  let quote = byContract;
  if (contractLocked && paymentLocked) quote = "both";
  else if (paymentLocked) quote = "payment";

  return {
    appointment: byContract,
    address: byContract,
    clientName: byContract,
    quote,
    contractTemplate: contractStatus === "signed" ? "signed" : byContract,
  };
};
